import React, { useState } from "react";
import moment from "moment";
import { Link } from "react-router-dom";
import BeatLoader from "react-spinners/BeatLoader";
import { Box, Typography } from "@mui/material";
import { toast } from "react-toastify";
import Modal from "@mui/material/Modal";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { MdClose } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import {
  createTheme,
  ThemeProvider,
  alpha,
  styled,
} from "@mui/material/styles";
import { DataGridPro, gridClasses, GridToolbar } from "@mui/x-data-grid-pro";

import "../../StudySetup.css";
import JoditEditor from "jodit-react";
import {
  getLoadEditDataFooterServices,
  getLoadEditDataHeaderServices,
  getLoadEditDataSignaturePageServices,
  getReportsServices,
  SaveReportsLayout,
} from "../../../../../services/reports";
import { FaCheckSquare, FaEdit, FaEye, FaPencilAlt, FaRegSquare } from "react-icons/fa";

const getMuiTheme = createTheme({
  typography: {
    fontSize: 25,
  },
  ".css-1x51dt5-MuiInputBase-input-MuiInput-input": {
    color: "#000000 !important",
  },
});

const notify = () => toast.success("Report layout saved successfully!");

const ODD_OPACITY = 0.2;

const StripedDataGrid = styled(DataGridPro)(({ theme }) => ({
  [`& .${gridClasses.row}.even`]: {
    backgroundColor: theme.palette.grey[200],
    "&:hover, &.Mui-hovered": {
      backgroundColor: alpha(theme.palette.primary.main, ODD_OPACITY),
      "@media (hover: none)": {
        backgroundColor: "transparent",
      },
    },
    "&.Mui-selected": {
      backgroundColor: alpha(
        theme.palette.primary.main,
        ODD_OPACITY + theme.palette.action.selectedOpacity
      ),
      "&:hover, &.Mui-hovered": {
        backgroundColor: alpha(
          theme.palette.primary.main,
          ODD_OPACITY +
            theme.palette.action.selectedOpacity +
            theme.palette.action.hoverOpacity
        ),
        // Reset on touch devices, it doesn't add specificity
        "@media (hover: none)": {
          backgroundColor: alpha(
            theme.palette.primary.main,
            ODD_OPACITY + theme.palette.action.selectedOpacity
          ),
        },
      },
    },
  },
}));

const Reports = () => {
  const [load, setLoad] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [editorContent, setEditorContent] = useState("");
  const [editData, setEditData] = useState({});
  const [showDialog, setShowDialog] = React.useState(false);

  const [studyReports, setStudyReports] = useState([]);
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = useState(5);

  React.useEffect(() => {
    fetchReportData(page + 1, pageSize);
  }, [page, pageSize]);

  const navigate = useNavigate();

  const handleOpenModal = async (title, params) => {
    console.log(params, "96");
    
    setModalTitle(title);
    setEditorContent(params?.value);
    setModalOpen(true);

    await fetchLoadEditData(params?.row.reportId, title);
  };

  function onChange(value) {
    if (!value) return;
    setEditorContent(value);
  }

  const handleCloseModal = () => setModalOpen(false);

  const fetchReportData = async () => {
    try {
      setLoad(true);
      const res = await getReportsServices();
      if (res.status) {
        setStudyReports(res.data.result);
      }
      setLoad(false);
    } catch (err) {
      setLoad(false);
      console.error("Error fetching email content:", err.message);
      requestFailed(err.message);
    }
  };

  const fetchLoadEditData = async (id, title) => {
    try {
      setLoad(true);

      let res;
      if (title === "Edit Header") {
        res = await getLoadEditDataHeaderServices(id);
      } else if (title === "Edit Footer") {
        res = await getLoadEditDataFooterServices(id);
      } else if (title === "Edit Signature Page") {
        res = await getLoadEditDataSignaturePageServices(id);
      }

      if (res?.status) {
        setEditData(res.data.result);
        setEditorContent(res.data.result.layoutHtml);
      }

      setLoad(false);
    } catch (err) {
      setLoad(false);
      console.error("Error fetching content:", err.message);
      toast.error("Failed to load content.");
    }
  };

  const rows = studyReports.map((row) => ({ id: row.reportId, ...row }));

  const columns = [
    {
      field: "id",
      hide: true,
      headerName: "ID",
      width: 0,
      renderHeader: () => <div className="grid-heading-id">{"ID"}</div>,
      renderCell: (params) => {
        const id = params.row.id;

        return (
          <div className="grid-id">
            <p>{id}</p>
          </div>
        );
      },
    },
    {
      field: "procName",
      headerName: "Report Name",
      width: 280,
      renderHeader: () => <div className="grid-heading">{"Proc Name"}</div>,
      renderCell: (params) => {
        const reportName = params.row.procName;

        return (
          <div className="grid-body">
            <p>{reportName}</p>
          </div>
        );
      },
    },
    {
      field: "reportTitle",
      headerName: "Report Title",
      width: 280,
      renderHeader: () => <div className="grid-heading">{"Report Name"}</div>,
      renderCell: (params) => {
        const reportTitle = params.row.reportTitle;

        return (
          <div className="grid-body">
            <p>{reportTitle}</p>
          </div>
        );
      },
    },
    {
      field: "headers",
      headerName: "Headers",
      width: 280,
      renderHeader: () => <div className="grid-heading">{"Headers"}</div>,
      renderCell: (params) => {
        const headers = params.row.headers;

        return (
          <button
            style={{
              fontFamily: "Poppins",
              cursor: "pointer",
              textAlign: "center",
              color: "white",
              background:
                "linear-gradient(90.39deg, #3661eb 3.6%, #3661eb 98.93%), #79a1de",
              border: "none",
              borderRadius: "3px",
              fontWeight: "500",
              fontSize: "12px",
              padding: "6px 9px",
              fontStyle: "normal",
              textTransform: "capitalize",
            }}
            onClick={() => handleOpenModal("Edit Header", params)}
          >
            Edit Header
          </button>
        );
      },
    },
    {
      field: "footers",
      headerName: "Footers",
      width: 280,
      renderHeader: () => <div className="grid-heading">{"Footers"}</div>,
      renderCell: (params) => {
        const footers = params.row.footers;

        return (
          <button
            style={{
              fontFamily: "Poppins",
              cursor: "pointer",
              textAlign: "center",
              color: "white",
              background:
                "linear-gradient(90.39deg, #3661eb 3.6%, #3661eb 98.93%), #79a1de",
              border: "none",
              borderRadius: "3px",
              fontWeight: "500",
              fontSize: "12px",
              padding: "6px 9px",
              fontStyle: "normal",
              textTransform: "capitalize",
            }}
            onClick={() => handleOpenModal("Edit Footer", params)}
          >
            Edit Footer
          </button>
        );
      },
    },
    {
      field: "signaturePages",
      headerName: "Signature Pages",
      width: 280,
      renderHeader: () => (
        <div className="grid-heading">{"Signature Pages"}</div>
      ),
      renderCell: (params) => {
        const signaturePages = params.row.signaturePages;

        return (
          <button
            style={{
              fontFamily: "Poppins",
              cursor: "pointer",
              textAlign: "center",
              color: "white",
              background:
                "linear-gradient(90.39deg, #3661eb 3.6%, #3661eb 98.93%), #79a1de",
              border: "none",
              borderRadius: "3px",
              fontWeight: "500",
              fontSize: "12px",
              padding: "6px 9px",
              fontStyle: "normal",
              textTransform: "capitalize",
            }}
            onClick={() => handleOpenModal("Edit Signature Page", params)}
          >
            Edit Signature Page
          </button>
        );
      },
    },
    {
      field: "isActive",
      headerName: "Is Active",
      width: 280,
      renderHeader: () => <div className="grid-heading">{"Is Active"}</div>,
      renderCell: (params) => {
        const isActive = params.row.isActive;

        return (
          <button
            style={{
              textAlign: "center",
              background: "rgb(247, 243, 243)",
              border: "none",
              borderRadius: "5px",
            }}
          >
            {isActive ? (
              <FaCheckSquare
                color="#3661eb"
                style={{
                  fontSize: "15px",
                }}
              />
            ) : (
              <FaRegSquare
                color="rgb(140 140 140)"
                style={{
                  fontSize: "15px",
                }}
              />
            )}
          </button>
        );
      },
    },
    {
      field: "isBlinded",
      // hide: true,
      headerName: "Blinded",
      width: 150,
      renderHeader: () => <div className="grid-heading">{"Is Blinded"}</div>,
      renderCell: (params) => {
        const blinded = params.row.isBlinded;

        return (
          <button
            style={{
              textAlign: "center",
              background: "rgb(247, 243, 243)",
              border: "none",
              borderRadius: "5px",
            }}
          >
            {blinded ? (
              <FaCheckSquare
                color="#3661eb"
                style={{
                  fontSize: "15px",
                }}
              />
            ) : (
              <FaRegSquare
                color="rgb(140 140 140)"
                style={{
                  fontSize: "15px",
                }}
              />
            )}
          </button>
        );
      },
    },
    {
      field: "actions",
      flex: 1,
      sortable: false,
      filterable: false,
      minWidth: 250,
      renderHeader: () => <div className="grid-heading ms-4">{"Actions"}</div>,
      renderCell: (params) => {
        const { reportId, reportTitle, procName, id } = params.row;
        return (
          <div>
            <button
              style={{
                cursor: "pointer",
                textAlign: "center",
                backgroundColor: "#337ab7",
                padding: "8px",
                border: "none",
                borderRadius: "5px",
                marginLeft: "10px",
              }}
              onClick={() =>
                navigate(`/study-management/study-settings/edit-selected-report`, {
                  state: {
                    id: id,
                    reportId: reportId,
                    reportTitle: reportTitle,
                    procName: procName,
                  },
                })
              }
            >
              <FaEdit color="white" size={15} />
            </button>
            <button
              style={{
                cursor: "pointer",
                textAlign: "center",
                backgroundColor: "#337ab7",
                padding: "8px",
                border: "none",
                borderRadius: "5px",
                marginLeft: "10px",
              }}
              onClick={() =>
                navigate(`/study-management/study-settings/create-report`, {
                  state: {
                    reportId: reportId,
                    reportTitle: reportTitle,
                    procName: procName,
                  },
                })
              }
            >
              <FaPencilAlt color="white" size={15} />
            </button>
            <button
            className="bg-green-500"
              style={{
                cursor: "pointer",
                textAlign: "center",
                padding: "8px",
                border: "none",
                borderRadius: "5px",
                marginLeft: "10px",
              }}
              onClick={() =>
                navigate(`/study-management/study-settings/all-reports`, {
                  state: {
                    reportId: reportId,
                    reportTitle: reportTitle,
                    procName: procName,
                  },
                })
              }
            >
              <FaEye color="white" size={15} />
            </button>
          </div>
        );
      },
    },
  ];

  const requestFailed = (message) => {
    console.error("Request Failed:", message);
    toast.error(message);
  };

  const onSubmit = async () => {
    try {
      setLoad(true);
      setShowDialog(false);

      const updatedData = {
        id: editData.id,
        reportId: editData.reportId,
        layoutHtml: editorContent,
        reportLayoutTypeId: editData.reportLayoutTypeId,
      };

      const res = await SaveReportsLayout(updatedData);

      if (res.status == 200) {
        console.log(res, "300");
        setLoad(false);
        notify();
        setModalOpen(false);
      }
    } catch (err) {
      setLoad(false);
      setShowDialog(false);
      console.log("Error: ", err.message);
      requestFailed(err.message);
    }
  };

  return (
    <>
      {load ? (
        <>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "70vh",
            }}
          >
            <BeatLoader color="#3661eb" />
          </div>
        </>
      ) : (
        <div className="content-body">
          <p className="study-management-link" style={{ fontWeight: "600" }}>
            <Link to="/study-management">Manage Your Study</Link> | 
            <Link to="/study-management/study-settings/reports">Report Layout</Link>|{" "}
            <Link to="#">Selected Reports</Link>
          </p>
          <Row style={{ marginTop: "2%", marginBottom: "2%" }}>
            <Col md={6}>
              <div className="study-management-head-start">
                <p className="study-management-heading">Selected Reports</p>
              </div>
            </Col>
          </Row>

          <Box sx={{ height: 400, width: "100%", display: "flex" }}>
            <ThemeProvider theme={getMuiTheme}>
              <StripedDataGrid
                className="allQuestionnaire-grid"
                rows={rows}
                columns={columns}
                pageSize={pageSize}
                onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                rowsPerPageOptions={[5, 10, 20]}
                components={{ Toolbar: GridToolbar }}
                pagination
                rowHeight={38}
                disableSelectionOnClick
                experimentalFeatures={{ newEditingApi: true }}
                getRowClassName={(params) =>
                  params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
                }
              />
            </ThemeProvider>
          </Box>

          <Row style={{ marginTop: "2%" }}>
            <Col md={6}></Col>
            <Col md={6}>
              <Link to="/study-management/study-settings/reports">
                <div className="study-management-head-end">
                  <button className="study-management-create-btn-lg">
                    Back To Report Layout
                  </button>
                </div>
              </Link>
            </Col>
          </Row>
          <Modal
            open={modalOpen}
            onClose={handleCloseModal}
            sx={{ zIndex: "13000" }}
          >
            <Box
              sx={{
                p: 3,
                width: 800,
                bgcolor: "background.paper",
                m: "auto",
                mt: 10,
                borderRadius: 2,
                maxHeight: "500px",
                overflowY: "auto",
              }}
            >
              <Box display="flex" justifyContent="space-between" gap={2} mt={2}>
                <Typography variant="h6" gutterBottom>
                  {modalTitle}
                </Typography>
                <div
                  className="text-[16px] mt-[-26px] cursor-pointer text-gray-500 hover:text-gray-700 transition-all"
                  onClick={handleCloseModal}
                >
                  <MdClose fontSize={22} className="font-bold" />
                </div>
              </Box>
              <JoditEditor value={editorContent} onBlur={onChange} />

              <Box display="flex" gap={2} mt={2}>
                <button
                  style={{
                    fontFamily: "Poppins",
                    cursor: "pointer",
                    textAlign: "center",
                    color: "white",
                    background:
                      "linear-gradient(90.39deg, #3661eb 3.6%, #3661eb 98.93%), #79a1de",
                    border: "none",
                    borderRadius: "3px",
                    fontWeight: "500",
                    fontSize: "12px",
                    padding: "6px 9px",
                    fontStyle: "normal",
                    textTransform: "capitalize",
                  }}
                  onClick={onSubmit}
                >
                  Save
                </button>
                <button
                  style={{
                    fontFamily: "Poppins",
                    cursor: "pointer",
                    textAlign: "center",
                    color: "#3661eb",
                    background: "transparent",
                    border: "1px solid",
                    borderRadius: "3px",
                    fontWeight: "500",
                    fontSize: "12px",
                    padding: "6px 9px",
                    fontStyle: "normal",
                    textTransform: "capitalize",
                  }}
                  onClick={handleCloseModal}
                >
                  Cancel
                </button>
              </Box>
            </Box>
          </Modal>
        </div>
      )}
    </>
  );
};

export default Reports;
